import moment from 'moment-jalaali';
import {
  CourseType,
  EventType,
  LessonType,
  SearchDataType,
  SearchOptionType,
} from 'types/course.type';
import userImage from 'public/user.svg';
import fa from 'locales/fa.json';
import {
  CourseRoute,
  EventRoute,
  HomeRoute,
  ProfileRoute,
  TermRoute,
} from 'services/routes';
import { siteURL } from 'services/request';

export const CATEGORIES = [
  'علوم عقلی',
  'قرآن و حدیث',
  'آداب و سنن » (فقه اصول)',
  'سبک زندگی',
  'کلامی و اعتقادی',
  'تاریخ',
  'اخلاقی',
  'مهارتی',
];

export const BadgeCategory = (type): { name: string; color: string } | undefined => {
  // see antd badge color : https://ant.design/components/badge/
  const types = [
    { name: fa.global.homat, color: 'cyan' },
    { name: fa.global.doat, color: 'green' },
    { name: fa.global.roat, color: 'magenta' },
    { name: fa.global.event, color: 'geekblue' },
    { name: fa.global.qoran, color: 'gold' },
    { name: fa.global.term, color: 'purple' },
  ];

  return types.find((item) => item.name === type);
};

/**
 *  0123456789 => ۰۱۲۳۴۵۶۷۸۹
 * @param {string | number} number
 * @returns {string}
 */
export const faNumber = (value: string | number): string => {
  if (value === null || value === undefined) return '';

  const toFaDict = {
    0: '۰',
    1: '۱',
    2: '۲',
    3: '۳',
    4: '۴',
    5: '۵',
    6: '۶',
    7: '۷',
    8: '۸',
    9: '۹',
  };
  const letters = value.toString().split('');
  const arr = letters.map((item) => (item in toFaDict ? toFaDict[item] : item));
  return arr.join('');
};
export const engNumber = (value: string | number): string => {
  if (value === null || value === undefined) return '';

  const toEngDict = {
    '۰': 0,
    '۱': 1,
    '۲': 2,
    '۳': 3,
    '۴': 4,
    '۵': 5,
    '۶': 6,
    '۷': 7,
    '۸': 8,
    '۹': 9,
  };
  const letters = value.toString().split('');
  const arr = letters.map((item) => (item in toEngDict ? toEngDict[item] : item));
  return arr.join('');
};

/**
 * Convert dash-separated string to camelCase
 * @param {string} : dash-separated-string
 * @returns {string} : dashSeparatedString
 */
export const camelCase = (input: string): string =>
  input &&
  (input.slice(0, 1).toLowerCase() + input.slice(1))
    .replace(/([-_ ]){1,}/g, ' ')
    .split(/[-_ ]/)
    .reduce((cur, acc) => cur + acc[0].toUpperCase() + acc.substring(1));

/**
 * Convert camelCase string to kebab-case
 * @param {string} : dashSeparatedString
 * @returns {string} :  dash-separated-string
 */
export const kebabCase = (input: string): string =>
  input &&
  input
    .split(/(?=[A-Z])/)
    .join('-')
    .toLocaleLowerCase();

export const calcTime = (time: number): string => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time - hours * 3600) / 60);
  const secondes = time - hours * 3600 - minutes * 60;
  return hours
    ? `${faNumber(secondes)} : ${faNumber(minutes)} : ${faNumber(hours)}`
    : `${faNumber(secondes)} : ${faNumber(minutes)}`;
};

export const fileSize = (size: number | string): string =>
  Number(size) > 1000000
    ? `${faNumber((Number(size) / 1000000).toFixed(1))}MB`
    : Number(size) > 1000
    ? `${faNumber(Math.floor(Number(size) / 1000))}KB`
    : `${faNumber(Math.floor(Number(size)))}B`;

export const generateOptions = (
  searchData: SearchDataType,
  termId: number,
): SearchOptionType[] => {
  if (!searchData) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (searchData.workshops as any)?.concat(
    searchData.events.map((item) => ({ ...item, webinar: true })),
  );

  const teachers = [
    ...new Set(
      data
        ?.map((item) => item.teachers.map((k) => k.nickname))
        .flat()
        .filter((k) => !!k && k),
    ),
  ];

  const teacherOptions = teachers.map((name) => {
    const target = data
      ?.find((item) => item.teachers.some((teacher) => teacher.nickname === name))
      .teachers.find((teacher) => teacher.nickname === name);
    return {
      name: name || target.family,
      id: target.id,
      href: ProfileRoute(target.id, name || target.family),
      isTeacher: true,
      avatar: target.avatar || userImage,
    };
  });

  const coursesOptions =
    data.map((item) => ({
      name: item.title,
      id: item.id,
      href: item.webinar
        ? EventRoute(item.id, item.title)
        : CourseRoute(item.id, item.title),
      category: item.webinar ? fa.global.event : item.category?.title,
      webinar: item.webinar,
      term: item.term,
    })) || [];

  const homeRoute = { name: fa.global.home, home: true, id: 1, href: HomeRoute() };
  const termRoute = { name: fa.global.term, term: true, href: TermRoute(termId, 'term') };

  return [homeRoute, termRoute, ...coursesOptions, ...teacherOptions];
};

export const ellipsisText = (text, length = 40): string => {
  return text?.length > length ? `${text.slice(0, length)}...` : text;
};

export const PLAYER_CONTROLS = [
  'play',
  'progress',
  'current-time',
  'mute',
  'volume',
  'settings',
  'fullscreen',
];

// convert Gregorian date to jalaali
export const convertToJalaliDate = (date: string | Date): string => {
  return (
    (date && moment(date, 'YYYY-MM-DD').locale('en').format('jYYYY/jMM/jDD').trim()) ||
    null
  );
};

// convert jalaali date to Gregorian
export const convertJalaliToGregorian = (date: string): string => {
  return (
    (date && moment(date, 'jYYYY/jjM/jjD').locale('en').format('YYYY-MM-DD').trim()) ||
    null
  );
};

export const pageTitle = (title): string => {
  return `${title || ''} | تعالی`;
};

export const minLengthError = (number): string => {
  return `حداقل ${faNumber(number)} حرف وارد کنید.`;
};
export const maxLengthError = (number): string => {
  return `حداکثر ${faNumber(number)} حرف وارد کنید.`;
};

export const seoHref = (): {
  home: string;
  privacy: string;
  termRegister: string;
  download: string;
  consulting: string;
} => ({
  home: siteURL,
  download: `${siteURL}/download/`,
  consulting: `${siteURL}/consulting/`,
  termRegister: `${siteURL}/term-overview/`,
  privacy: `${siteURL}/privacy/`,
});

export const getToday = (): string =>
  convertToJalaliDate(new Date().toISOString().slice(0, 10))
    .split('/')
    .map((num) => Number(num))
    .join('/');

export const jsonLD = (
  title: string,
  description: string,
  url: string,
): string => `{"@context": "https://schema.org/",
  "@type": "webpage", "name": '${title}',
  "description": '${description}',
  "url": '${url}'}`;

export const workshopJsonLD = (course: CourseType, url: string): string => `{
  "@context": "https://schema.org",
  "@type": "Course",
  "name": "${course.title}",
  "url": "${url}",
  "description": "${
    course.description_plain_text ? course.description_plain_text.replace(/"/g, "'") : ''
  }",
  "offers": {
    "@type": "Offer",
    "category": "Paid",
    "price": "${course.price}",
    "priceCurrency": "IRR"
  },
  "image":"${course.image}",
  "publisher": {"@type": "Organization", "name": "Taalei", "sameAs": "${siteURL}"},
  "provider": {"@type": "Organization", "name": "Taalei", "sameAs": "${siteURL}"},
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "${course.rate}",
    "ratingCount": "${course.rate_count}"
  },
  "hasCourseInstance": [
      {
        "@type": "CourseInstance",
        "courseMode": "Online",
        "offers": {
          "@type": "Offer",
          "price": "${course.price}",
          "priceCurrency": "IRR"
        },
        "location": "https://taalei-edu.ir/${course.id}",
        "courseWorkload": "P7D",
        "instructor": [{
          "@type": "Person",
          "name": "${course.teachers[0].nickname}",
          "description": "${course.teachers[0].nickname}",
          "image": "${course.teachers[0].avatar}"
        }]
      }
  ]
}`;

export const eventJsonLD = (event: EventType, url: string): string => `{
  "@context": "http://schema.org",
  "@type": "Course",
  "name": "${event.title}",
  "url": "${url}",
  "description": "${event.description}",
  "image":"${event.image}",
  "publisher": {"@type": "Organization", "name": "Taalei", "sameAs": "${siteURL}"},
  "provider": {"@type": "Organization", "name": "Taalei", "sameAs": "${siteURL}"},
  "hasCourseInstance": {
    "@type": "CourseInstance", 
    "courseMode": "Online", 
    "instructor": {"@type": "Person", "name": "${event.teachers[0].nickname}"}
  }
}`;

export const lessonJsonLD = (
  course: CourseType,
  url: string,
  courseUrl: string,
  title: string,
  lesson: LessonType,
): string => `{
  "@context": "http://schema.org",
  "@type": "Course",
  "name": "${course?.title}",
  "url": "${courseUrl}",
  "@id": "${url}",
  "description": "${
    course.description_plain_text ? course.description_plain_text.replace(/"/g, "'") : ''
  }",
  "image":"${course?.image}",
  "provider": {"@type": "Organization", "name": "Taalei", "sameAs": "${siteURL}"},
  "hasCourseInstance": {
    "@type": "CourseInstance", 
    "courseMode": "Online", 
    "name": "${title}",
    "description": "${lesson?.description}",
    "contentUrl": "${lesson?.files[0].file}",
    "duration": "${lesson?.time}",
    "instructor": {"@type": "Person", "name": "${course?.teachers[0].nickname}"}
  }
}`;

export const appJsonLD = (): string => `{
    "@context": "https://schema.org",
    "@type": "Corporation",
    "name": "taalei-edu",
    "url": "${siteURL}",
    "logo": "${siteURL}/taalei-edu/images/main-logo-taalei.webp",
    "alternateName": "مدرسه تعالی",
    "sameAs": [
        "https://www.instagram.com/taalei_edu/",
        "https://t.me/taalei_edu"
    ],
    "contactPoint": [
        {
            "@type": "ContactPoint",
            "telephone": "+989900014090",
            "contactType": "customer service",
            "contactOption": [
                "TollFree",
                "HearingImpairedSupported"
            ],
            "areaServed": "IR",
            "availableLanguage": "FA"
        }
    ]
}`;

export const FAVORITES = [
  { id: 1, name: fa.global.qoran },
  { id: 2, name: fa.global.hadis },
  { id: 3, name: fa.global.akhlagh },
  { id: 4, name: fa.global.history },
];

export const googleGTag = (): string => `  window.dataLayer = window.dataLayer || [];
function gtag(){window.dataLayer.push(arguments);}
gtag('js', new Date());

gtag('config', 'G-MBF6MCY415');`;

export const googleTagManager = (): string => `
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','G-VDL3CF9XCX');`;

export const yektanet = (): string => `
!function (t, e, n) {
  t.yektanetAnalyticsObject = n, t[n] = t[n] || function () {
      t[n].q.push(arguments)
  }, t[n].q = t[n].q || [];
  var a = new Date, r = a.getFullYear().toString() + "0" + a.getMonth() + "0" + a.getDate() + "0" + a.getHours(),
      c = e.getElementsByTagName("script")[0], s = e.createElement("script");
  s.id = "ua-script-2vdZ15sV"; s.dataset.analyticsobject = n;
  s.async = 1; s.type = "text/javascript";
  s.src = "https://cdn.yektanet.com/rg_woebegone/scripts_v3/2vdZ15sV/rg.complete.js?v=" + r, c.parentNode.insertBefore(s, c)
}(window, document, "yektanet");`;

export const googleTagManagerBody =
  (): string => `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NF8J9JL"
  height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

export const sortByDates = <T extends { date: string }>(data: T[]): T[] => {
  return data.sort((a, b) => {
    const date1 = new Date(
      a.date.replace(/(\d{4})\/(\d{2})\/(\d{2})/, '$1-$2-$3'),
    ).getTime();
    const date2 = new Date(
      b.date.replace(/(\d{4})\/(\d{2})\/(\d{2})/, '$1-$2-$3'),
    ).getTime();

    return date2 - date1;
  });
};

export const consultingRules = (isOnline?): string =>
  isOnline
    ? `
بسم الله الرحمن الرحیم

آیین نامه مشاوره آنلاین مخاطبین واحد مشاوره موسسه تعالی
1. برای شرکت در مشاوره لازم است اطلاعات خواسته شده را دقیق وارد نمایید. در غیر اینصورت مدرسه تعالی مسئولیتی در قبال مشاوره ندارد.
2. رعایت ادب و احترام متقابل نسبت به مشاور از موارد لازم الاجرا میباشد.

3. پس از تعیین زمان مشاوره لازم است به موقع در جلسه مشاوره شرکت نمایید در غیر اینصورت مسئولیتی جهت برگزاری مجدد جلسه  یا عودت وجه متوجه موسسه تعالی نیست.
4. . هزینه هر جلسه مشاوره ‌با توجه به موضوع،نوع و مشاور متفاوت می‌باشد که از طریق کیف پول دریافت میشود.

5. مدت هر جلسه مشاوره حداکثر 45 دقیقه خواهد بود و بعد از آن مشاوره باید به اتمام برسد.

6. جهت بررسی کیفیت جلسات مشاوره، این جلسه توسط سیستم ضبط می‌شود. (نتایج مشاوره نزد موسسه محفوظ خواهد ماند و در اختیار دیگران قرار نمی‌گیرد)
7.در هنگام انتخاب موضوع مشاوره و انتخاب مشاور دقت لازم را داشته باشید،که پس از آن امکان تغییر نمی باشد..
واحد مشاوره موسسه تعالی همواره صمیمانه بهترین ها را برای کاربران خود از خداوند باری تعالی مسئلت دارد.

`
    : `
بسم الله الرحمن الرحیم

آیین نامه مشاوره آفلاین مخاطبین واحد مشاوره موسسه تعالی
1. برای شرکت در مشاوره لازم است اطلاعات خواسته شده را دقیق وارد نمایید. در غیر اینصورت موسسه تعالی مسئولیتی در قبال مشاوره ندارد.
2. رعایت ادب و احترام متقابل نسبت به مشاور از موارد لازم الاجرا میباشد.

3. مشاور حداکثر تا ۲۴ ساعت آینده جواب متنی پیام شما را خواهد داد،از صبوری و وقت شناسی شما سپاسگزاریم.
4. هزینه هر جلسه مشاوره ‌با توجه به موضوع،نوع و مشاور متفاوت می‌باشد که از طریق کیف پول دریافت میشود.
5.سوالات شما فقط در یک موضوع خاص و یک مورد واحد باید صورت گیرد و سقف تعداد سوالات ۱۰ سوال میباشد.

6.در هنگام انتخاب موضوع مشاوره و انتخاب مشاور دقت لازم را داشته باشید،که پس از آن امکان تغییر نمی باشد.
7.سعی شود در حد الامکان سوالات کوتاه،واضح و بدون غلط املایی مطرح شود.
واحد مشاوره موسسه تعالی همواره صمیمانه بهترین ها را برای کاربران خود از خداوند باری تعالی مسئلت دارد.
`;

export const enamadScript = (): string => `
<a referrerpolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=424749&Code=Ckbmwt4hmdcNaVUbOYBQrG8iCsw0Tf0X'><img referrerpolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=424749&Code=Ckbmwt4hmdcNaVUbOYBQrG8iCsw0Tf0X' alt='' style='cursor:pointer' code='Ckbmwt4hmdcNaVUbOYBQrG8iCsw0Tf0X'></a>
`;
